import { Box, Button, Link, Typography, Card, CardContent, CardMedia, Accordion as MuiAccordion, AccordionSummary, AccordionDetails, Paper } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

interface Props {
    blocks: PageBlock[];
}

const PageBlocksRenderer = ({ blocks }: Props) => {
    if (!blocks || blocks.length === 0) {
        return null;
    }

    const getVideoEmbedUrl = (url: string): string => {
        // Convert YouTube/Vimeo URLs to embed format
        if (url.includes("youtube.com") || url.includes("youtu.be")) {
            const videoId = url.includes("youtu.be")
                ? url.split("youtu.be/")[1]?.split("?")[0]
                : url.split("v=")[1]?.split("&")[0];
            return `https://www.youtube.com/embed/${videoId}`;
        }
        if (url.includes("vimeo.com")) {
            const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url;
    };

    const renderBlock = (block: PageBlock) => {
        switch (block.type) {
            case PageBlockType.TEXT:
                return (
                    <Box
                        key={block.id}
                        sx={{ mb: 2 }}
                        dangerouslySetInnerHTML={{ __html: (block as TextBlock).content.html }}
                    />
                );
            case PageBlockType.IMAGE:
                const imageBlock = block as ImageBlock;
                return (
                    <Box key={block.id} sx={{ mb: 2, textAlign: "center" }}>
                        <img
                            src={imageBlock.content.url}
                            alt={imageBlock.content.alt}
                            style={{ maxWidth: "100%", height: "auto" }}
                        />
                        {imageBlock.content.caption && (
                            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                                {imageBlock.content.caption}
                            </Typography>
                        )}
                    </Box>
                );
            case PageBlockType.BUTTON:
                const buttonBlock = block as ButtonBlock;
                return (
                    <Box key={block.id} sx={{ mb: 2 }}>
                        <Button
                            variant={buttonBlock.content.variant}
                            color={buttonBlock.content.color}
                            href={buttonBlock.content.url}
                        >
                            {buttonBlock.content.text}
                        </Button>
                    </Box>
                );
            case PageBlockType.LINK:
                const linkBlock = block as LinkBlock;
                return (
                    <Box key={block.id} sx={{ mb: 2 }}>
                        <Link href={linkBlock.content.url} target="_blank" rel="noopener">
                            {linkBlock.content.text}
                        </Link>
                    </Box>
                );
            case PageBlockType.GRID:
                const gridBlock = block as GridBlock;
                return (
                    <Box
                        key={block.id}
                        sx={{
                            mb: 2,
                            display: "grid",
                            gridTemplateColumns: `repeat(${gridBlock.content.columns}, 1fr)`,
                            gap: 2,
                        }}
                    >
                        {gridBlock.content.blocks.map((columnBlocks, colIndex) => (
                            <Box key={colIndex}>
                                {columnBlocks.map((nestedBlock) => renderBlock(nestedBlock))}
                            </Box>
                        ))}
                    </Box>
                );
            case PageBlockType.SECTION:
                const sectionBlock = block as SectionBlock;
                return (
                    <Box
                        key={block.id}
                        sx={{
                            mb: 2,
                            p: 3,
                            backgroundColor: sectionBlock.content.backgroundColor || "transparent",
                            borderRadius: 1,
                        }}
                    >
                        {sectionBlock.content.title && (
                            <Typography variant="h4" sx={{ mb: 2 }}>
                                {sectionBlock.content.title}
                            </Typography>
                        )}
                        {sectionBlock.content.blocks.map((nestedBlock) => renderBlock(nestedBlock))}
                    </Box>
                );
            case PageBlockType.HERO:
                const heroBlock = block as HeroBlock;
                return (
                    <Box
                        key={block.id}
                        sx={{
                            mb: 2,
                            position: "relative",
                            height: heroBlock.content.height || "400px",
                            backgroundImage: heroBlock.content.backgroundImage
                                ? `url(${heroBlock.content.backgroundImage})`
                                : "none",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            textAlign: heroBlock.content.textAlign || "center",
                            borderRadius: 1,
                            overflow: "hidden",
                        }}
                    >
                        {heroBlock.content.backgroundOverlay && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: heroBlock.content.backgroundOverlay,
                                }}
                            />
                        )}
                        <Box sx={{ position: "relative", zIndex: 1, p: 4 }}>
                            <Typography variant="h2" sx={{ mb: 2, fontWeight: "bold" }}>
                                {heroBlock.content.title}
                            </Typography>
                            {heroBlock.content.subtitle && (
                                <Typography variant="h5" sx={{ mb: 3 }}>
                                    {heroBlock.content.subtitle}
                                </Typography>
                            )}
                            {heroBlock.content.buttonText && heroBlock.content.buttonUrl && (
                                <Button
                                    variant="contained"
                                    size="large"
                                    href={heroBlock.content.buttonUrl}
                                    sx={{ mt: 2 }}
                                >
                                    {heroBlock.content.buttonText}
                                </Button>
                            )}
                        </Box>
                    </Box>
                );
            case PageBlockType.CARD:
                const cardBlock = block as CardBlock;
                return (
                    <Card key={block.id} sx={{ mb: 2 }}>
                        {cardBlock.content.image && cardBlock.content.imagePosition === "top" && (
                            <CardMedia
                                component="img"
                                image={cardBlock.content.image}
                                alt={cardBlock.content.title}
                            />
                        )}
                        <Box
                            sx={{
                                display: cardBlock.content.imagePosition !== "top" ? "flex" : "block",
                                flexDirection:
                                    cardBlock.content.imagePosition === "right" ? "row-reverse" : "row",
                            }}
                        >
                            {cardBlock.content.image && cardBlock.content.imagePosition !== "top" && (
                                <CardMedia
                                    component="img"
                                    image={cardBlock.content.image}
                                    alt={cardBlock.content.title}
                                    sx={{ width: (cardBlock.content.imagePosition === "left" || cardBlock.content.imagePosition === "right") ? 200 : "auto" }}
                                />
                            )}
                            <CardContent>
                                <Typography variant="h5" component="div" gutterBottom>
                                    {cardBlock.content.title}
                                </Typography>
                                {cardBlock.content.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {cardBlock.content.description}
                                    </Typography>
                                )}
                                {cardBlock.content.buttonText && cardBlock.content.buttonUrl && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        href={cardBlock.content.buttonUrl}
                                    >
                                        {cardBlock.content.buttonText}
                                    </Button>
                                )}
                            </CardContent>
                        </Box>
                    </Card>
                );
            case PageBlockType.VIDEO:
                const videoBlock = block as VideoBlock;
                return (
                    <Box key={block.id} sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                position: "relative",
                                paddingBottom: "56.25%",
                                height: 0,
                                overflow: "hidden",
                                borderRadius: 1,
                            }}
                        >
                            <iframe
                                src={getVideoEmbedUrl(videoBlock.content.url)}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    border: 0,
                                }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </Box>
                        {videoBlock.content.caption && (
                            <Typography variant="caption" sx={{ display: "block", mt: 1, textAlign: "center" }}>
                                {videoBlock.content.caption}
                            </Typography>
                        )}
                    </Box>
                );
            case PageBlockType.SPACER:
                const spacerBlock = block as SpacerBlock;
                return (
                    <Box
                        key={block.id}
                        sx={{
                            height: spacerBlock.content.height || "50px",
                        }}
                    />
                );
            case PageBlockType.DIVIDER:
                const dividerBlock = block as DividerBlock;
                return (
                    <Box
                        key={block.id}
                        sx={{
                            mb: 2,
                            borderTop: `${dividerBlock.content.thickness || "1px"} ${
                                dividerBlock.content.style || "solid"
                            } ${dividerBlock.content.color || "#cccccc"}`,
                        }}
                    />
                );
            case PageBlockType.CTA:
                const ctaBlock = block as CTABlock;
                return (
                    <Paper
                        key={block.id}
                        sx={{
                            mb: 2,
                            p: 4,
                            textAlign: "center",
                            backgroundColor: ctaBlock.content.backgroundColor || "primary.light",
                        }}
                    >
                        <Typography variant="h4" gutterBottom>
                            {ctaBlock.content.title}
                        </Typography>
                        {ctaBlock.content.description && (
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                {ctaBlock.content.description}
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            size="large"
                            href={ctaBlock.content.buttonUrl}
                        >
                            {ctaBlock.content.buttonText}
                        </Button>
                    </Paper>
                );
            case PageBlockType.TESTIMONIAL:
                const testimonialBlock = block as TestimonialBlock;
                return (
                    <Paper
                        key={block.id}
                        sx={{
                            mb: 2,
                            p: 4,
                            position: "relative",
                        }}
                    >
                        <FormatQuoteIcon
                            sx={{
                                position: "absolute",
                                top: 16,
                                left: 16,
                                fontSize: 40,
                                color: "primary.light",
                                opacity: 0.3,
                            }}
                        />
                        <Typography variant="body1" sx={{ mb: 3, fontStyle: "italic", pl: 4 }}>
                            "{testimonialBlock.content.quote}"
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            {testimonialBlock.content.authorImage && (
                                <img
                                    src={testimonialBlock.content.authorImage}
                                    alt={testimonialBlock.content.author}
                                    style={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                    }}
                                />
                            )}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {testimonialBlock.content.author}
                                </Typography>
                                {testimonialBlock.content.authorTitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {testimonialBlock.content.authorTitle}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                );
            case PageBlockType.ACCORDION:
                const accordionBlock = block as AccordionBlock;
                return (
                    <Box key={block.id} sx={{ mb: 2 }}>
                        {accordionBlock.content.items.map((item, index) => (
                            <MuiAccordion key={index}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>{item.title}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography>{item.content}</Typography>
                                </AccordionDetails>
                            </MuiAccordion>
                        ))}
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box>
            {blocks.map((block) => renderBlock(block))}
        </Box>
    );
};

export default PageBlocksRenderer;
